import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Play, Settings, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Dither from '../components/Dither';

const SpecRow = ({ label, title, description, isLast, index }) => (
    <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
        className={`col-span-12 grid grid-cols-1 md:grid-cols-12 ${!isLast ? 'border-b-brut' : ''}`}
    >
        <div className="col-span-12 md:col-span-2 p-6 md:p-8 border-b-brut md:border-b-0 md:border-r-brut flex items-start">
            <span className="font-mono text-[10px] text-muted uppercase tracking-widest">{label}</span>
        </div>
        
        <div className="col-span-12 md:col-span-4 p-6 md:p-8 border-b-brut md:border-b-0 md:border-r-brut flex items-center">
            <h3 className="text-3xl md:text-5xl font-heading uppercase leading-none tracking-tight text-foreground">
                {title}
            </h3>
        </div>
        
        <div className="col-span-12 md:col-span-6 p-6 md:p-8 flex items-center">
            <p className="font-mono text-xs text-foreground uppercase leading-relaxed max-w-lg">
                {description}
            </p>
        </div>
    </motion.div>
);

export default function BaseApp() {
  
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'monochrome');
    root.classList.add('dark');
    window.scrollTo(0,0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Base | Digital Architecture for the Modern Student</title>
        <meta name="description" content="Base is a highly polished, native-feeling extension of the iPhone that brings your agenda and tasks directly to your Lock Screen." />
        <meta name="keywords" content="Base App, Mora Tadeo, Calendar App, Student Planner, iOS Widget, Live Activities, TestFlight" />
        
        {/* Open Graph / Social Media Meta Tags */}
        <meta property="og:title" content="Base | Digital Architecture for the Modern Student" />
        <meta property="og:description" content="Base is a highly polished, native-feeling extension of the iPhone that brings your agenda and tasks directly to your Lock Screen." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://moratadeo.com/base" />
        <meta property="og:site_name" content="Mora Tadeo" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base | Digital Architecture for the Modern Student" />
        <meta name="twitter:description" content="Base is a highly polished, native-feeling extension of the iPhone that brings your agenda and tasks directly to your Lock Screen." />
      </Helmet>

      <section className="w-full bg-background grid-sys min-h-screen relative overflow-hidden z-10 pt-24 md:pt-32 border-x border-border max-w-[1920px] mx-auto">
        
        <div className="col-span-12 p-6 border-b-brut bg-background relative z-20">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-muted hover:text-accent transition-colors flex items-center gap-2 w-fit">
                &larr; RETURN TO INDEX
            </Link>
        </div>

        <div className="col-span-12 p-6 md:p-16 border-b-brut min-h-[50vh] flex flex-col justify-center relative z-10 overflow-hidden">
            {/* Dither Shape behind the Base title */}
            <div className="absolute top-1/2 right-12 -translate-y-1/2 w-[80vw] h-[80vw] md:w-[40vw] md:h-[40vw] rounded-full overflow-hidden z-0 opacity-60 border border-white/5 shadow-[0_0_50px_rgba(56,189,248,0.15)] pointer-events-auto">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.4}
                    waveFrequency={2.5}
                    waveSpeed={0.06}
                />
            </div>

            <motion.h1 
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="text-6xl md:text-8xl lg:text-[10vw] font-heading font-extrabold uppercase leading-[0.85] tracking-tighter text-foreground max-w-[90rem] mb-6 relative z-10"
            >
                BASE.
            </motion.h1>
            <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="font-mono text-xs md:text-sm text-accent uppercase tracking-[0.2em] max-w-2xl relative z-10"
            >
                DIGITAL ARCHITECTURE FOR THE MODERN STUDENT.
            </motion.p>
        </div>

        <div className="col-span-12 grid grid-cols-1 md:grid-cols-12 border-b-brut bg-background">
            <div className="col-span-12 md:col-span-5 p-6 md:p-12 border-b-brut md:border-b-0 md:border-r-brut flex flex-col justify-center">
                <h3 className="text-4xl md:text-6xl font-heading uppercase leading-none tracking-tight text-foreground mb-6">
                    A NEW PARADIGM.
                </h3>
                <p className="font-mono text-xs text-muted uppercase leading-relaxed max-w-md">
                    Base is not just another calendar. Built explicitly for college students, it is a highly polished, native-feeling extension of the iPhone that brings your agenda and tasks directly to your Lock Screen. We eliminate schedule anxiety by surfacing exactly what you need, exactly when you need it.
                </p>
            </div>
            <div className="col-span-12 md:col-span-7 p-6 md:p-16 bg-[#09090b] flex items-center justify-center relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-accent/10 blur-[100px] rounded-full pointer-events-none"></div>
                
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md aspect-[9/19] bg-[#1C1C1E] rounded-[3rem] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-6 relative overflow-hidden flex flex-col"
                >
                    <div className="flex justify-between items-end pt-8 pb-4 mb-4 border-b border-white/5">
                        <h2 className="text-3xl font-extrabold text-white tracking-tight">Agenda</h2>
                        <div className="font-semibold text-xs text-[#8E8E93] tracking-widest uppercase">SEP 2026</div>
                    </div>
                    
                    <div className="flex justify-between mb-8">
                        {['13','14','15','16','17'].map((d, i) => (
                            <div key={d} className="flex flex-col items-center gap-2">
                            <span className="text-[10px] font-semibold text-[#8E8E93]">
                                {['S','M','T','W','T'][i]}
                            </span>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                d === '15' ? 'bg-[#0A84FF] text-white shadow-[0_0_15px_rgba(10,132,255,0.4)]' : 'text-white'
                            }`}>
                                {d}
                            </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-black/50 p-4 rounded-2xl flex items-center gap-4 border border-white/5 shadow-inner">
                        <div className="w-10 h-10 bg-[#0A84FF]/20 rounded-xl flex items-center justify-center text-[#0A84FF]">
                            <BookOpen size={20} />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-bold text-sm text-white">Class</h4>
                            <div className="text-[10px] font-semibold text-[#8E8E93] flex items-center gap-2 mt-1">
                                <span>2:00 PM</span>
                                <span>•</span>
                                <span>50m</span>
                                <span>•</span>
                                <span>CCB</span>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-max">
                        <div className="flex items-center gap-2 px-3 py-2 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full">
                            <div className="px-3 text-[#8E8E93]"><Calendar size={16} /></div>
                            <div className="bg-[#0A84FF] text-white p-3 rounded-full shadow-[0_4px_14px_rgba(10,132,255,0.4)] relative">
                                <Play size={18} fill="currentColor" className="ml-0.5" />
                            </div>
                            <div className="px-3 text-[#8E8E93]"><Settings size={16} /></div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>

        <div className="col-span-12 flex justify-between items-center p-6 md:p-8 border-b-brut bg-black/40 backdrop-blur-md relative z-10">
            <span className="font-mono text-xs font-bold tracking-widest uppercase text-accent">
                CORE ARCHITECTURE
            </span>
            <span className="font-mono text-[10px] text-muted tracking-widest uppercase hidden md:block">
                SYSTEM SPECS
            </span>
        </div>

        <div className="col-span-12 bg-black/20 backdrop-blur-sm relative z-10">
            <SpecRow 
                index={0}
                label="01. THE DAILY AGENDA"
                title="UNIFIED TIMELINE"
                description="An infinitely scrollable week view. Classes, meetings, and standard tasks exist in a single unified list. Distinct visual categorization ensures you know exactly what comes next."
                isLast={false}
            />
            <SpecRow 
                index={1}
                label="02. LIVE ACTIVITIES"
                title="TRACK ALL ENGINE"
                description="Push your entire agenda to the iOS system level. A beautifully formatted, auto-updating widget sits on the Lock Screen, keeping you perfectly on track without ever opening the app."
                isLast={false}
            />
            <SpecRow 
                index={2}
                label="03. ARCHITECTURE"
                title="100% LOCAL PRIVACY"
                description="No accounts required. Built with React Native (Expo) and seamlessly bridged to native Swift for uncompromising Lock Screen widget performance. All data is saved strictly on-device."
                isLast={true}
            />
        </div>

        {/* ADDITIONAL SECTIONS: THE STACK & CTA */}
        
        <div className="col-span-12 grid grid-cols-1 md:grid-cols-12 border-t-brut">
            <div className="col-span-12 md:col-span-6 p-6 md:p-16 border-b-brut md:border-b-0 md:border-r-brut flex flex-col justify-center">
                <span className="font-mono text-xs font-bold tracking-widest uppercase text-accent mb-4">
                    TECHNOLOGY STACK
                </span>
                <h3 className="text-4xl md:text-6xl font-heading uppercase leading-none tracking-tight text-foreground mb-8">
                    BUILT FOR SPEED.
                </h3>
                
                <div className="flex flex-col gap-6 font-mono text-xs text-foreground uppercase tracking-wider">
                    <div className="flex justify-between border-b border-white/10 pb-2">
                        <span className="text-muted">UI FRAMEWORK</span>
                        <span>REACT NATIVE (EXPO)</span>
                    </div>
                    <div className="flex justify-between border-b border-white/10 pb-2">
                        <span className="text-muted">WIDGET ENGINE</span>
                        <span>SWIFT / WIDGETKIT</span>
                    </div>
                    <div className="flex justify-between border-b border-white/10 pb-2">
                        <span className="text-muted">DATA PERSISTENCE</span>
                        <span>SHARED WIDGET STORAGE</span>
                    </div>
                    <div className="flex justify-between border-b border-white/10 pb-2">
                        <span className="text-muted">ANIMATIONS</span>
                        <span>REANIMATED 3</span>
                    </div>
                </div>
            </div>
            
            <a id="get-base" href="https://testflight.apple.com/join/dgnYjDHS" target="_blank" rel="noopener noreferrer" className="col-span-12 md:col-span-6 flex flex-col group cursor-pointer relative overflow-hidden bg-background">
                <div className="flex-1 p-6 md:p-16 flex flex-col justify-center items-center text-center relative z-10">
                    <div className="w-24 h-24 rounded-full border border-border flex items-center justify-center transition-all duration-700 overflow-hidden mb-8 relative group-hover:scale-[1.3] group-hover:border-accent/50 group-hover:shadow-[0_0_30px_rgba(56,189,248,0.2)]">
                        {/* Dither Effect Background */}
                        <div className="absolute inset-0 z-0">
                            <Dither
                                waveColor={[56/255, 189/255, 248/255]} 
                                disableAnimation={false}
                                enableMouseInteraction
                                mouseRadius={0.5}
                                colorNum={4}
                                waveAmplitude={0.4}
                                waveFrequency={2}
                                waveSpeed={0.05}
                            />
                        </div>
                        {/* Slight blur over dither, under icon for visibility */}
                        <div className="absolute inset-0 z-[5] bg-black/10 backdrop-blur-[2px]"></div>
                        {/* Download Icon overlay */}
                        <svg className="w-10 h-10 text-foreground relative z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] group-hover:text-white transition-colors duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                    </div>
                    <h2 className="text-5xl md:text-7xl font-heading font-extrabold uppercase leading-[0.85] tracking-tighter text-foreground group-hover:text-accent transition-colors duration-500">
                        GET BASE.
                    </h2>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mt-4 group-hover:text-foreground transition-colors duration-500">
                        JOIN THE TESTFLIGHT BETA
                    </p>
                </div>
            </a>
        </div>

    </section>
    </>
  );
}
